<template>
  <div class="popUpWrap">
    <section class="popup light memberInvite">
      <div class="topArea">
        <h4>
          {{ t("organization-Invite-members") }}
          <span class="count">{{ invitedMemberList.length }}</span>
        </h4>
        <div class="exit" @click.stop="dialogStore.close()"></div>
      </div>

      <div class="scrollY">
        <h5>{{ t("organization-Invite-members-description") }}</h5>

        <LazyOrganizationMemberInvitedForm
          v-for="(member, index) in invitedMemberList"
          :key="member.uid"
          :base-id="member.uid"
          :email="member.email"
          :role="member.organizationRole"
          :menu="member.accessRoles"
          :email-errors="member.emailErrors"
          :menu-error="member.menuError"
          :role-error="member.roleError"
          @update:email="updateMember(index, { email: $event })"
          @update:role="updateMember(index, { organizationRole: $event ?? null })"
          @update:menu="updateMember(index, { accessRoles: $event ?? [] })"
        >
          <template #remove>
            <div v-if="invitedMemberList.length > 1" class="btnArea">
              <button type="button" class="btn deleteCircle" @click.stop="openRemoveConfirm(index)">
                <span></span>
              </button>
            </div>
          </template>
        </LazyOrganizationMemberInvitedForm>

        <div class="btArea">
          <button class="btn blueBgBtn" @click.stop="addInviteMember">
            <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 10 10" fill="none">
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M5 0C5.55228 0 6 0.447715 6 1V4.03125H9C9.55229 4.03125 10 4.47897 10 5.03125C10 5.58353 9.55229 6.03125 9 6.03125H6V9C6 9.55229 5.55228 10 5 10C4.44772 10 4 9.55229 4 9V6.03125H1C0.447715 6.03125 0 5.58353 0 5.03125C0 4.47897 0.447715 4.03125 1 4.03125H4V1C4 0.447715 4.44772 0 5 0Z"
                fill="#1A61EA"
              />
            </svg>
            {{ t("organization-member-add") }}
          </button>
        </div>

        <div class="btnArea">
          <LazySafetyRequestButton tag="button" class="btn" :class="{disabled: !canInvite}" :disabled="!canInvite"
                                   :safety-click="onClickInvite">
            {{ t("common-invite") }}
          </LazySafetyRequestButton>
        </div>
      </div>
    </section>

    <section v-if="removeConfirm.open" class="popup dark basic">
      <div class="topArea">
        <div class="exit" @click.stop="closeRemoveConfirm"></div>
      </div>
      <div class="scrollY">
        <div class="textArea flex">
          <h5>{{ t("organization-member-remove-title") }}</h5>
        </div>
        <div class="btnArea">
          <button class="cancel" @click.stop="closeRemoveConfirm">{{ t("common-cancel") }}</button>
          <button type="button" @click.stop="confirmRemove">{{ t("common-remove") }}</button>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { computed, reactive, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useDialogStore } from "~/store/dialog/dialogStore";
import { useToastStore } from "~/store/toast/toastStore";
import { useOrganizationService } from "~/composables/service/useOrganizationService";
import { useUtilsStore } from "~/store/utils/utilsStore";

import { OrganizationInviteMemberModel } from "~~/package/mapper/organization/model/OrganizationInviteMemberModel";
import type OrganizationInviteResponse from "~~/package/apis/organization/response/OrganizationInviteResponse";
import type { MemberMenuAccessRole } from "~~/package/mapper/organization/model/OrganizationMemeberTypes";
import type { UserV2Type } from "~~/package/mapper/user/model/UserType";
import { ToastType } from "~/components/Common/Toast/model/ToastType";

type InvitationErrorType = "ALREADY_ORG_MEMBER" | "ALREADY_INVITED";
type FailedInvitation = OrganizationInviteResponse["failedInvitations"][number];

type InviteMemberUI = OrganizationInviteMemberModel & {
  uid: string;
  emailErrors: string[];
  roleError: boolean;
  menuError: boolean;
  accessRoles?: MemberMenuAccessRole[];
  organizationRole?: UserV2Type | null;
};

type MemberPatch = Partial<{
  email: string;
  organizationRole: UserV2Type | null;
  accessRoles: MemberMenuAccessRole[];
}>;

type ValidateInvitesResult = {
  ok: boolean;
  failedCount: number;
};

const { t } = useI18n();
const dialogStore = useDialogStore();
const toastStore = useToastStore();
const utilsStore = useUtilsStore();
const organizationService = useOrganizationService();

const inviteErrorMessageMap: Record<InvitationErrorType, string> = {
  ALREADY_ORG_MEMBER: t("organization-Invite-members-email-error-1"),
  ALREADY_INVITED: t("organization-Invite-members-email-error-2"),
};

const mapErrorTypesToMessages = (errorTypes: string[] | undefined): string[] => {
  return (errorTypes ?? [])
      .filter((type): type is InvitationErrorType => type === "ALREADY_ORG_MEMBER" || type === "ALREADY_INVITED")
      .map((type) => inviteErrorMessageMap[type]);
};

const removeConfirm = reactive<{ open: boolean; index: number | null }>({
  open: false,
  index: null,
});

const openRemoveConfirm = (index: number) => {
  removeConfirm.open = true;
  removeConfirm.index = index;
};

const closeRemoveConfirm = () => {
  removeConfirm.open = false;
  removeConfirm.index = null;
};

const createEmptyMember = (): InviteMemberUI => {
  const base = OrganizationInviteMemberModel.create({ uid: crypto.randomUUID() }) as InviteMemberUI;

  base.uid = base.uid ?? crypto.randomUUID();
  base.emailErrors = [];
  base.roleError = false;
  base.menuError = false;

  return base;
};

const invitedMemberList = ref<InviteMemberUI[]>([createEmptyMember()]);

const addInviteMember = () => {
  invitedMemberList.value.push(createEmptyMember());
  toastStore.openToast({
    toastType: ToastType.SUCCESS,
    contents: t("organization-member-add-success"),
  });
};

const confirmRemove = () => {
  const idx = removeConfirm.index;
  if (idx === null) return;

  invitedMemberList.value = invitedMemberList.value.filter((_, i) => i !== idx);

  toastStore.openToast({
    toastType: ToastType.SUCCESS,
    contents: t("organization-member-remove-success"),
  });

  closeRemoveConfirm();
};

const resetMemberErrors = (m: InviteMemberUI) => {
  m.emailErrors = [];
  m.roleError = false;
  m.menuError = false;
};

const updateMember = (index: number, patch: MemberPatch) => {
  const m = invitedMemberList.value[index];
  if (!m) return;

  if (patch.email !== undefined) {
    m.email = patch.email;
    m.emailErrors = [];
  }

  if (patch.organizationRole !== undefined) {
    m.organizationRole = patch.organizationRole;
    m.roleError = false;
  }

  if (patch.accessRoles !== undefined) {
    m.accessRoles = patch.accessRoles;
    m.menuError = false;
  }
};

const hasMemberErrors = (m: InviteMemberUI): boolean => {
  return m.emailErrors.length > 0 || m.roleError || m.menuError;
};

const validateMember = (m: InviteMemberUI): boolean => {
  resetMemberErrors(m);
  let ok = true;

  const email = (m.email ?? "").trim();

  if (!email) {
    m.emailErrors.push(t("error-null-email"));
    ok = false;
  } else if (!emailValidation(email)) {
    m.emailErrors.push(t("error-invalid-email"));
    ok = false;
  } else if (utilsStore.isBlockedEmailDomain(email)) {
    m.emailErrors.push(t("error-personal-email"));
    ok = false;
  }

  if (m.organizationRole === null || m.organizationRole === undefined) {
    m.roleError = true;
    ok = false;
  }

  if (!m.accessRoles || m.accessRoles.length === 0) {
    m.menuError = true;
    ok = false;
  }

  return ok;
};

const applyDuplicateEmailErrors = () => {
  const normalized = invitedMemberList.value
      .map((m) => (m.email ?? "").trim().toLowerCase())
      .filter(Boolean);

  const dupSet = new Set(normalized.filter((e, i) => normalized.indexOf(e) !== i));
  if (dupSet.size === 0) return;

  const msg = t("organization-Invite-members-email-error-3");

  invitedMemberList.value.forEach((m) => {
    const key = (m.email ?? "").trim().toLowerCase();
    if (!key || !dupSet.has(key)) return;

    if (!m.emailErrors.includes(msg)) m.emailErrors.push(msg);
  });
};

const validateInvitesForSubmit = (): ValidateInvitesResult => {
  invitedMemberList.value.forEach(validateMember);
  applyDuplicateEmailErrors();

  const failedCount = invitedMemberList.value.filter(hasMemberErrors).length;
  return { ok: failedCount === 0, failedCount };
};

const isInviteButtonEnabledMember = (m: InviteMemberUI): boolean => {
  return m.organizationRole !== null && m.organizationRole !== undefined;
};

const canInvite = computed(() => {
  if (invitedMemberList.value.length === 0) return false;
  return invitedMemberList.value.every(isInviteButtonEnabledMember);
});

const applyInviteApiErrors = (failedInvitations: FailedInvitation[]) => {
  const byEmail = new Map<string, InviteMemberUI>();

  invitedMemberList.value.forEach((m) => {
    const key = (m.email ?? "").trim().toLowerCase();
    if (key) byEmail.set(key, m);
  });

  failedInvitations.forEach((f) => {
    const key = (f.email ?? "").trim().toLowerCase();
    const m = byEmail.get(key);
    if (!m) return;

    const messages = mapErrorTypesToMessages(f.errorTypes);
    if (messages.length === 0) return;

    m.emailErrors = Array.from(new Set([...m.emailErrors, ...messages]));
  });
};

const buildPayload = () => {
  return invitedMemberList.value.map((m) => ({
    email: (m.email ?? "").trim(),
    accessRoles: m.accessRoles ?? [],
    organizationRole: m.organizationRole ?? null,
  }));
};

const closeDialog = () => {
  dialogStore.options.close.event();
  dialogStore.close();
};

const onClickInvite = async (): Promise<void> => {
  const v = validateInvitesForSubmit();

  if (!v.ok) {
    toastStore.openToast({
      toastType: ToastType.FAILED,
      contents: convertToNextLine(t("organization-Invite-members-fail", { Count: v.failedCount })),
    });
    return;
  }

  const invitations = buildPayload();
  const count = invitations.length;

  try {
    const result = (await organizationService.inviteOrganizationMembers(invitations)) as OrganizationInviteResponse;

    toastStore.openToast({
      toastType: ToastType.SUCCESS,
      contents: t("organization-Invite-members-success", { Count: result.successCount ?? count }),
    });

    closeDialog();
  } catch (err: any) {
    const data = (err?.response?.data as { data?: OrganizationInviteResponse } | undefined)?.data;

    if (data?.failedInvitations && Array.isArray(data.failedInvitations)) {
      applyInviteApiErrors(data.failedInvitations);

      toastStore.openToast({
        toastType: ToastType.FAILED,
        contents: convertToNextLine(t("organization-Invite-members-fail", { Count: data.failedCount ?? 0 })),
      });
      return;
    }

    toastStore.openToast({
      toastType: ToastType.FAILED,
      contents: t("organization-Invite-members-fail", { Count: count }),
    });
  }
};
</script>

